import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import api from '../utils/api';
import { asList } from '../utils/asList';
import { formatSsLabel, resolveUnitTypeDisplayName } from '../utils/unitDisplay';
import {
  nextAutoUniqueIdForPrefix,
  parseUnitUniqueId,
  unitMatchesEquipmentType,
} from '../utils/unitIdFormat';

/**
 * Add another unit under the same equipment sub-type (e.g. 9-1-3 → 9-1-4).
 * Shown at the bottom of the unit hub Set System tab.
 */
export default function AddSiblingUnitForm({ building, unit, siblingUnits = [], onCreated }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const buildingId = building?.id;
  const instanceId = unit?.building_ss_instance_id;

  const { data: equipmentTypes = [] } = useQuery({
    queryKey: ['ss-equipment-types', buildingId, instanceId],
    queryFn: async () => {
      const res = await api.get(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types`
      );
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!buildingId && !!instanceId,
  });

  const equipmentType = useMemo(
    () =>
      equipmentTypes.find((t) => String(t.id) === String(unit?.equipment_type_id)) ||
      null,
    [equipmentTypes, unit?.equipment_type_id]
  );

  const parsed = parseUnitUniqueId(unit?.unique_id, unit?.ss_code);
  const typePrefix = equipmentType?.prefix || parsed.prefix || '';
  const typeDisplayName = resolveUnitTypeDisplayName(unit, { equipmentTypes });
  const specifiedSystemLabel = typePrefix
    ? `SS ${typePrefix}`
    : formatSsLabel(unit?.ss_code);

  const relatedUnits = useMemo(() => {
    if (!typePrefix && !equipmentType) return [];
    const escaped = String(typePrefix).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const childRe = typePrefix ? new RegExp(`^${escaped}-(\\d+)$`, 'i') : null;
    return siblingUnits.filter((u) => {
      if (!u) return false;
      if (equipmentType && unitMatchesEquipmentType(u, equipmentType)) return true;
      if (childRe && childRe.test(String(u.unique_id || ''))) return true;
      return false;
    });
  }, [siblingUnits, equipmentType, typePrefix]);

  const suggestedUniqueId = useMemo(
    () => (typePrefix ? nextAutoUniqueIdForPrefix(typePrefix, relatedUnits) : ''),
    [typePrefix, relatedUnits]
  );

  const [form, setForm] = useState({
    unique_id: suggestedUniqueId,
    location: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    unit_type: typeDisplayName || unit?.unit_type || '',
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      unique_id: suggestedUniqueId,
      unit_type: typeDisplayName || unit?.unit_type || prev.unit_type,
    }));
  }, [suggestedUniqueId, typeDisplayName, unit?.id]);

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post(
        `/buildings/${buildingId}/ss-instances/${instanceId}/units`,
        payload
      );
      return res.data?.data ?? res.data;
    },
    onSuccess: async (created) => {
      try {
        await api.post('/checklist-templates/build-default', { ssCode: unit.ss_code });
      } catch {
        /* optional ?? unit is already saved; checklist can be built from SS Units */
      }
      await queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
      await queryClient.invalidateQueries(['ss-unit']);
      toast.success('Unit added ?? worker app will pick it up on next sync');
      onCreated?.(created);
      if (created?.id) {
        navigate(`/buildings/${buildingId}/units/${created.id}`);
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add unit'),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!instanceId) {
      toast.error('SS instance missing');
      return;
    }
    const unitType = String(form.unit_type || '').trim() || typeDisplayName;
    const location = String(form.location || '').trim();
    const name = location || unitType || suggestedUniqueId;
    createMutation.mutate({
      unique_id: String(form.unique_id || '').trim() || undefined,
      name,
      unit_type: unitType,
      equipment_type_id: unit.equipment_type_id || equipmentType?.id || undefined,
      location,
      manufacturer: form.manufacturer || undefined,
      model: form.model || undefined,
      serial_number: form.serial_number || undefined,
    });
  };

  if (!unit?.id || !buildingId || !instanceId || !typePrefix) {
    return null;
  }

  return (
    <div className="mt-6 border-t border-slate-200 pt-4">
      <p className="text-lg font-semibold text-slate-900 mb-1">Add unit</p>
      <p className="text-xs text-gray-500 mb-4">
        Add another unit under {specifiedSystemLabel}. Checklist copies from this type; default IQP
        stays on the SS.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specified system *</label>
            <p className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-900 font-medium">
              {specifiedSystemLabel}
              {typeDisplayName ? (
                <span className="text-gray-500 font-normal"> · {typeDisplayName}</span>
              ) : null}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unique ID (auto if empty)
            </label>
            <input
              name="unique_id"
              value={form.unique_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg font-mono"
              placeholder={suggestedUniqueId || '9-1-4'}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location label</label>
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="e.g. Final Exits"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            name="manufacturer"
            value={form.manufacturer}
            onChange={handleChange}
            placeholder="Manufacturer"
            className="px-3 py-2 border rounded-lg"
          />
          <input
            name="model"
            value={form.model}
            onChange={handleChange}
            placeholder="Model"
            className="px-3 py-2 border rounded-lg"
          />
        </div>
        <input
          name="serial_number"
          value={form.serial_number}
          onChange={handleChange}
          placeholder="Serial number"
          className="w-full px-3 py-2 border rounded-lg"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Equipment category / unit type
          </label>
          <input
            name="unit_type"
            value={form.unit_type}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg bg-gray-50"
            readOnly
          />
        </div>
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {createMutation.isPending ? 'Adding…' : 'Add unit'}
          </button>
        </div>
      </form>
    </div>
  );
}
